'use client';

/**
 * GDPR/CCPA cookie consent modal — granular toggles for Necessary, Analytics,
 * and Advertising. Necessary is locked on; the other two default to off until
 * the user opts in. The modal auto-opens on first visit (when no decision
 * exists in localStorage) and can be re-opened from the Footer's "Cookie
 * settings" button via `useCookieConsent().openSettings()`.
 *
 * a11y: focus trap on the modal, Esc closes (only if already decided), aria
 * dialog role + labelled by the heading.
 */

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useT } from '@/lib/i18n';
import { useCookieConsent } from '@/lib/cookie-consent';

export default function CookieConsent() {
  const t = useT();
  const {
    modalOpen,
    decided,
    analytics: persistedAnalytics,
    advertising: persistedAdvertising,
    save,
    acceptAll,
    rejectNonEssential,
    closeModal,
  } = useCookieConsent();

  // Local toggle state — only commits to the persisted store when the user
  // hits "Save preferences". Seeded from current persisted values when the
  // modal opens so re-edits show what's currently active.
  const [analytics, setAnalytics] = useState(persistedAnalytics);
  const [advertising, setAdvertising] = useState(persistedAdvertising);

  useEffect(() => {
    if (modalOpen) {
      setAnalytics(persistedAnalytics);
      setAdvertising(persistedAdvertising);
    }
  }, [modalOpen, persistedAnalytics, persistedAdvertising]);

  // Esc closes (only when the user has already decided once — first-time
  // visitors must make a choice).
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && decided) closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, decided, closeModal]);

  // Move focus into the dialog on open so screen readers announce it.
  useEffect(() => {
    if (modalOpen && dialogRef.current) {
      const heading = dialogRef.current.querySelector('h2');
      if (heading instanceof HTMLElement) heading.focus();
    }
  }, [modalOpen]);

  if (!modalOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'oklch(0.2 0.01 260 / 0.45)',
        display: 'grid',
        placeItems: 'end center',
        padding: '0 0 0 0',
      }}
      onClick={(e) => {
        // Click on backdrop closes (only after decision made)
        if (e.target === e.currentTarget && decided) closeModal();
      }}
    >
      <div
        ref={dialogRef}
        style={{
          background: 'var(--bg-raised)',
          color: 'var(--ink)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
          maxWidth: 720,
          width: '100%',
          padding: '28px 28px 24px',
          boxShadow: 'var(--shadow-md)',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <h2
          id="cookie-consent-title"
          tabIndex={-1}
          style={{
            fontFamily: 'var(--font-headline)',
            fontSize: 26,
            margin: '0 0 8px',
            outline: 'none',
          }}
        >
          {t('cookie.title')}
        </h2>
        <p style={{ color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 20px' }}>
          {t('cookie.intro')}{' '}
          <Link
            href="/cookies"
            style={{ color: 'var(--accent)', textDecoration: 'underline' }}
            onClick={() => decided && closeModal()}
          >
            {t('cookie.learn_more')}
          </Link>
        </p>

        <div style={{ display: 'grid', gap: 12, margin: '0 0 24px' }}>
          <CategoryRow
            label={t('cookie.necessary_label')}
            description={t('cookie.necessary_desc')}
            checked
            disabled
            badge={t('cookie.always_on')}
            onChange={() => {}}
          />
          <CategoryRow
            label={t('cookie.analytics_label')}
            description={t('cookie.analytics_desc')}
            checked={analytics}
            onChange={setAnalytics}
          />
          <CategoryRow
            label={t('cookie.advertising_label')}
            description={t('cookie.advertising_desc')}
            checked={advertising}
            onChange={setAdvertising}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            className="ps-btn ps-btn-ghost"
            onClick={rejectNonEssential}
          >
            {t('cookie.reject_non_essential')}
          </button>
          <button
            type="button"
            className="ps-btn ps-btn-secondary"
            onClick={() => save({ analytics, advertising })}
          >
            {t('cookie.save_preferences')}
          </button>
          <button
            type="button"
            className="ps-btn ps-btn-primary"
            onClick={acceptAll}
          >
            {t('cookie.accept_all')}
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({
  label,
  description,
  checked,
  disabled,
  badge,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  badge?: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 16,
        alignItems: 'start',
        padding: '14px 16px',
        background: 'var(--bg-soft)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-lg)',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          <span>{label}</span>
          {badge && (
            <span
              style={{
                fontSize: 11,
                padding: '2px 8px',
                background: 'var(--bg-raised)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-full)',
                color: 'var(--ink-3)',
                fontWeight: 500,
              }}
            >
              {badge}
            </span>
          )}
        </div>
        <div style={{ color: 'var(--ink-2)', fontSize: 14, lineHeight: 1.5 }}>
          {description}
        </div>
      </div>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          width: 18,
          height: 18,
          marginTop: 4,
          accentColor: 'var(--accent)',
          cursor: disabled ? 'default' : 'pointer',
        }}
        aria-label={label}
      />
    </label>
  );
}
